/* initializeReactDatePicker import is needed
in order to properly map the react-with-styles based
component to support traditional classname mapping

https://github.com/airbnb/react-dates#initialize

In order to ensure that the initialize import is correctly
applied when the component is bundled at build time
we need to assign the default export to a named variable.
Otherwise, every consumer of the component
would need to manually import themselves.*/

//eslint-disable-next-line no-unused-vars
import initializeReactDatePicker from 'react-dates/initialize';

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { translationPropType } from '@eb/i18n';
import mapValues from 'lodash/mapValues';

import { moment } from '@eb/date';

import DayPickerRangeController from 'react-dates/lib/components/DayPickerRangeController';

import { Dropdown } from '@eb/eds-containers';
import { Icon } from '@eb/eds-icon';

import { datePropType, getAdditionalProps } from '@eb/eds-utils';
import { ISO_8601_DATE_FORMAT } from '@eb/eds-utils';

import { START_DATE, END_DATE, localizedPhrases } from '../constants';

import { ChevronLeftChunky } from '@eb/eds-iconography';
import { ChevronRightChunky } from '@eb/eds-iconography';

/*
react dates accepts only moment objects as date arguments and
asserts that if no value, `null` should be passed
 */
const _parseDateToMomentObject = (dateString) =>
    dateString ? moment(dateString) : null;

/* util to transform the returned moment object to our default date format string */
const _formatMomentObjectToISO = (momentObj) =>
    momentObj && momentObj.format(ISO_8601_DATE_FORMAT);

const _convertDates = (startDate, endDate) => ({
    startDate: _formatMomentObjectToISO(startDate),
    endDate: _formatMomentObjectToISO(endDate),
});

const ConditionalWrapper = ({ condition, wrapper, children }) =>
    condition ? wrapper(children) : children;

export default class RangePickerController extends PureComponent {
    static propTypes = {
        onDatesChange: PropTypes.func.isRequired,
        onClose: PropTypes.func,
        startDate: datePropType,
        endDate: datePropType,
        minDate: datePropType,
        maxDate: datePropType,
        focusedInput: PropTypes.oneOf([START_DATE, END_DATE]),
        onFocusChange: PropTypes.func,
        onClickOutside: PropTypes.func,

        /* Configuration Options for react-dates/DayPickerRangeController defaulted below */
        /**
         * Pixel height and width of individual days
         */
        daySize: PropTypes.number,
        /**
         * Moment formatting string for how the week days should be displayed
         */
        weekDayFormat: PropTypes.string,
        /**
         * Button to navigate to previous month
         */
        navPrev: PropTypes.node,
        /**
         * Button to navigate to next month
         */
        navNext: PropTypes.node,
        /**
         * Transition duration for changing month animation
         */
        transitionDuration: PropTypes.number,
        /**
         * Whether we wrap the calendar in a dropdown
         */
        hasDropDown: PropTypes.bool,
        /**
         * Bool to hide the keyboard shortcuts info panel
         */
        hideKeyboardShortcutsPanel: PropTypes.bool,
        /**
         * Number of months that should be displayed at one time
         */
        numberOfMonths: PropTypes.number,
        /**
         * Minimum selectable range size
         */
        minimumNights: PropTypes.number,
        /**
         * Informational aria-labels applied to the days within the calendar
         */
        phrases: PropTypes.objectOf(
            PropTypes.oneOfType([
                PropTypes.string,
                translationPropType,
                PropTypes.func,
            ]),
        ),
    };

    static defaultProps = {
        focusedInput: null,
        startDate: null,
        endDate: null,
        hasDropDown: true,

        /* Defaulted configuration options for react-dates/DayPickerRangeController */

        /* A11y label configuration */
        phrases: localizedPhrases,

        /* Defaults for consistent calendar design appearance. Customize at own discretion */
        daySize: 42,
        weekDayFormat: 'dd',
        navPrev: <Icon type={<ChevronLeftChunky />} title="previous" />,
        navNext: <Icon type={<ChevronRightChunky />} title="next" />,
        transitionDuration: 0,
        hideKeyboardShortcutsPanel: true,
        numberOfMonths: 1,
        minimumNights: 0,
    };

    _isOutsideRange = (date) => {
        const { minDate, maxDate } = this.props;

        const isOutsideRange =
            (minDate && date.isBefore(minDate)) ||
            (maxDate && date.isAfter(maxDate));

        return isOutsideRange;
    };

    _handleOnDatesChange = ({ startDate, endDate }) => {
        const { onDatesChange } = this.props;

        onDatesChange(_convertDates(startDate, endDate));
    };

    _handleOnClose = ({ startDate, endDate }) => {
        const { onClose } = this.props;

        if (onClose) {
            onClose(_convertDates(startDate, endDate));
        }
    };

    _getInitialMonth = () => {
        /* Initial rendered month should match
        that of the associated input's value */
        const { startDate, endDate, focusedInput } = this.props;

        let momentObjToReturn = moment();

        if (startDate && focusedInput === START_DATE) {
            momentObjToReturn = moment(startDate);
        }

        if (endDate && focusedInput === END_DATE) {
            momentObjToReturn = moment(endDate);
        }

        return momentObjToReturn;
    };

    render() {
        const {
            daySize,
            weekDayFormat,
            navPrev,
            navNext,
            transitionDuration,
            hideKeyboardShortcutsPanel,
            numberOfMonths,
            minimumNights,
            phrases,
            focusedInput,
            onFocusChange,
            startDate,
            endDate,
            hasDropDown,
            onClickOutside: handleClickOutside,
        } = this.props;

        if (!focusedInput) {
            return null;
        }

        const extraAttrs = getAdditionalProps(this);

        const formattedStartDate = _parseDateToMomentObject(startDate);
        const formattedEndDate = _parseDateToMomentObject(endDate);
        const translatedPhrases = mapValues(phrases, (phrase) => {
            if (typeof phrase === 'function') {
                return phrase;
            }
            return phrase.toString();
        });

        return (
            <div className="eds-date-picker__controller">
                <ConditionalWrapper
                    condition={hasDropDown}
                    wrapper={(children) => (
                        <Dropdown
                            onClickOutside={handleClickOutside}
                            align="left"
                        >
                            {children}
                        </Dropdown>
                    )}
                >
                    <DayPickerRangeController
                        {...extraAttrs}
                        onClose={this._handleOnClose}
                        onDatesChange={this._handleOnDatesChange}
                        isOutsideRange={this._isOutsideRange}
                        initialVisibleMonth={this._getInitialMonth}
                        startDate={formattedStartDate}
                        endDate={formattedEndDate}
                        focusedInput={focusedInput}
                        onFocusChange={onFocusChange}
                        daySize={daySize}
                        weekDayFormat={weekDayFormat}
                        navPrev={navPrev}
                        navNext={navNext}
                        transitionDuration={transitionDuration}
                        hideKeyboardShortcutsPanel={hideKeyboardShortcutsPanel}
                        numberOfMonths={numberOfMonths}
                        minimumNights={minimumNights}
                        phrases={translatedPhrases}
                    />
                </ConditionalWrapper>
            </div>
        );
    }
}
